import { useState, useMemo } from 'react';
import { PageHeader } from '../molecules/PageHeader';
import { Card } from '../atoms/Card';
import { CryptoSelect } from '../atoms/CryptoSelect';
import { FormField } from '../atoms/FormField';
import { usePayouts } from '../../utils/hooks/usePayouts';
import { useTokenPrices } from '../../utils/hooks/useTokenPrices';
import { getCurrentCryptoPrice } from '../../utils/coingecko';
import type { CryptoCurrency } from '../../utils/coingecko';
import { Select } from '../atoms/Select';
import { Input } from '../atoms/Input';
import { Button } from '../atoms/Button';
import { type TokenData } from '../../utils/dashboard';

interface PortfolioValue {
  crypto: CryptoCurrency;
  amount: number;
  currentPrice: number;
  value: number;
  acquisitionCost: number;
}

interface CryptoForSale {
  crypto: CryptoCurrency;
  totalTokens: number;
  currentPrice: number;
  averageAcquisitionPrice: number;
  totalAcquisitionCost: number;
}

export const SimulatorFlatTaxPage = () => {
  const [additionalMode, setAdditionalMode] = useState<'none' | 'global' | 'individual'>('none');
  const [additionalGlobalAmount, setAdditionalGlobalAmount] = useState<number>(0);
  const [additionalCryptos, setAdditionalCryptos] = useState<PortfolioValue[]>([]);
  const [selectedCrypto, setSelectedCrypto] = useState<CryptoCurrency | null>(null);
  const [selectedCryptoForSale, setSelectedCryptoForSale] = useState<string>('');
  const [tokensToSell, setTokensToSell] = useState<number>(0);
  const [, setCryptoPriceLoading] = useState(false);

  // Load data with useQuery
  const payoutsQuery = usePayouts();
  const tokenPricesQuery = useTokenPrices(payoutsQuery.data);

  const payouts = payoutsQuery.data || [];
  const payoutTokens = (tokenPricesQuery.data as TokenData[]) || [];
  const loading = payoutsQuery.isLoading || tokenPricesQuery.isLoading;

  const { totalPortfolioValue, totalAcquisitionCost } = useMemo(() => {
    const payoutValue = payoutTokens.reduce((sum, token) => {
      const value = Number(token.currentTotalValue) || 0;
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
    const payoutCost = payoutTokens.reduce((sum, token) => {
      const cost = Number(token.totalValueAtPurchase) || 0;
      return sum + (isNaN(cost) ? 0 : cost);
    }, 0);

    if (additionalMode === 'global') {
      const totalValue = Number(additionalGlobalAmount) || 0;
      return {
        totalPortfolioValue: totalValue,
        totalAcquisitionCost: payoutCost
      };
    } else if (additionalMode === 'individual') {
      const additionalValue = additionalCryptos.reduce((sum, crypto) => {
        const value = Number(crypto.value) || 0;
        return sum + (isNaN(value) ? 0 : value);
      }, 0);
      return {
        totalPortfolioValue: payoutValue + additionalValue,
        totalAcquisitionCost: payoutCost
      };
    } else {
      return {
        totalPortfolioValue: payoutValue,
        totalAcquisitionCost: payoutCost
      };
    }
  }, [payoutTokens, additionalMode, additionalGlobalAmount, additionalCryptos]);

  const bncChargesPaid = useMemo(() => {
    // BNC charges only apply to mining revenue (payout, stacking), not purchases or sales
    return payouts.filter(p => p.type === 'payout' || p.type === 'stacking').reduce((sum, payout) => {
      const bncRate = 0.34;
      const value = Number(payout.totalValue) || 0;
      const bncCharge = isNaN(value) ? 0 : value * bncRate;
      return sum + (isNaN(bncCharge) ? 0 : bncCharge);
    }, 0);
  }, [payouts]);

  const availableCryptosForSale = useMemo<CryptoForSale[]>(() => {
    const cryptoMap = new Map<string, CryptoForSale>();

    payoutTokens.forEach(token => {
      cryptoMap.set(token.crypto.id, {
        crypto: token.crypto,
        totalTokens: token.totalAmount || 0,
        currentPrice: token.currentPrice || 0,
        averageAcquisitionPrice: token.averagePurchasePrice || 0,
        totalAcquisitionCost: token.totalValueAtPurchase || 0
      });
    });

    additionalCryptos.forEach(crypto => {
      const existing = cryptoMap.get(crypto.crypto.id);
      if (existing) {
        const totalTokens = existing.totalTokens + crypto.amount;
        const totalAcquisitionCost = existing.totalAcquisitionCost + 0;
        cryptoMap.set(crypto.crypto.id, {
          ...existing,
          totalTokens,
          averageAcquisitionPrice: totalTokens > 0 ? totalAcquisitionCost / totalTokens : 0,
          totalAcquisitionCost
        });
      } else {
        cryptoMap.set(crypto.crypto.id, {
          crypto: crypto.crypto,
          totalTokens: crypto.amount,
          currentPrice: crypto.currentPrice,
          averageAcquisitionPrice: 0,
          totalAcquisitionCost: 0
        });
      }
    });

    return Array.from(cryptoMap.values());
  }, [payoutTokens, additionalCryptos]);

  const partialSaleCalculation = useMemo(() => {
    if (!selectedCryptoForSale || tokensToSell <= 0) {
      return {
        saleValue: 0,
        acquisitionCost: 0,
        plusValue: 0,
        flatTax: 0
      };
    }

    const selectedCrypto = availableCryptosForSale.find(c => c.crypto.id === selectedCryptoForSale);
    if (!selectedCrypto) {
      return {
        saleValue: 0,
        acquisitionCost: 0,
        plusValue: 0,
        flatTax: 0
      };
    }

    const saleValue = tokensToSell * selectedCrypto.currentPrice;
    const acquisitionCost = tokensToSell * selectedCrypto.averageAcquisitionPrice;
    const plusValue = Math.max(0, saleValue - acquisitionCost);
    const flatTax = plusValue * 0.314; // 31.4% flat tax (depuis janvier 2026)

    return {
      saleValue,
      acquisitionCost,
      plusValue,
      flatTax
    };
  }, [selectedCryptoForSale, tokensToSell, availableCryptosForSale]);

  const handleAddCrypto = () => {
    if (selectedCrypto) {
      setAdditionalCryptos([...additionalCryptos, {
        crypto: selectedCrypto,
        amount: 0,
        currentPrice: 0,
        value: 0,
        acquisitionCost: 0
      }]);
      setSelectedCrypto(null);
    }
  };

  const updateAdditionalCrypto = async (index: number, field: keyof PortfolioValue, value: string | number) => {
    const updated = [...additionalCryptos];

    if (field === 'amount') {
      const amount = Number(value) || 0;
      updated[index].amount = amount;
      updated[index].value = (updated[index].currentPrice || 0) * amount;

      if (updated[index].currentPrice === 0 && updated[index].crypto.id) {
        setCryptoPriceLoading(true);
        try {
          const price = await getCurrentCryptoPrice(updated[index].crypto.id);
          if (price !== null && !isNaN(price)) {
            updated[index].currentPrice = price;
            updated[index].value = amount * price;
          } else {
            updated[index].currentPrice = 0;
            updated[index].value = 0;
          }
        } catch (error) {
          console.error('Error fetching crypto price:', error);
          updated[index].currentPrice = 0;
          updated[index].value = 0;
        } finally {
          setCryptoPriceLoading(false);
        }
      }
    }

    setAdditionalCryptos(updated);
  };

  const removeAdditionalCrypto = (index: number) => {
    setAdditionalCryptos(additionalCryptos.filter((_, i) => i !== index));
  };

  if (loading && payouts.length === 0) {
    return (
      <div className="container mx-auto px-4 pt-8 pb-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Chargement des données...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-8 pb-8">
      <PageHeader
        title="Simulateur Flat Tax"
        subtitle="Simulez la flat tax 31.4% sur vos ventes de cryptos"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Section Configuration */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Configuration du Portfolio</h2>

          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-medium text-blue-900 mb-2">Portfolio depuis vos payouts</h3>
              <div className="space-y-2">
                {payoutTokens.map((token, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span>{token.crypto.name} ({token.crypto.symbol})</span>
                    <span className="font-medium">
                      {token.currentTotalValue.toLocaleString('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      })}
                    </span>
                  </div>
                ))}
              </div>
              {payoutTokens.length === 0 && (
                <p className="text-sm text-blue-600">Aucun payout trouvé</p>
              )}
              <div className="pt-2 border-t border-blue-200 mt-2">
                <div className="flex justify-between font-semibold">
                  <span>Valeur des payouts:</span>
                  <span>
                    {payoutTokens.reduce((sum, token) => sum + (Number(token.currentTotalValue) || 0), 0).toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: 'EUR'
                    })}
                  </span>
                </div>
              </div>
            </div>

            <Select
              label="Ajouter des montants manquants"
              value={additionalMode}
              onChange={(e) => setAdditionalMode(e.target.value as 'none' | 'global' | 'individual')}
              options={[
                { value: 'none', label: 'Utiliser uniquement les payouts/achats' },
                { value: 'global', label: 'Saisir valeur totale du portfolio' },
                { value: 'individual', label: 'Ajouter des cryptos manquantes une à une' }
              ]}
            />

            {additionalMode === 'global' ? (
              <div className="space-y-4">
                <div className="bg-orange-50 p-4 rounded-lg">
                  <FormField label="Valeur totale du portfolio (EUR)">
                    <Input
                      type="number"
                      value={additionalGlobalAmount}
                      onChange={(e) => {
                        const value = Number(e.target.value) || 0;
                        setAdditionalGlobalAmount(isNaN(value) ? 0 : value);
                      }}
                      placeholder="Montant total du portfolio incluant payout/achat"
                      min="0"
                      step="0.01"
                    />
                  </FormField>
                </div>
              </div>
            ) : additionalMode === 'individual' ? (
              <div className="space-y-4">
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h4 className="font-medium text-orange-900 mb-2">Ajouts manuels de cryptos</h4>
                  <p className="text-sm text-orange-600 mb-3">
                    Coût d&apos;acquisition = 0€ → flat tax sur 100% de la valeur
                  </p>
                </div>

                {additionalCryptos.map((crypto, index) => (
                  <div key={index} className="p-4 border rounded-lg bg-orange-50">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Crypto</label>
                        <div className="mt-1 text-sm font-medium">
                          {crypto.crypto.name} ({crypto.crypto.symbol})
                        </div>
                      </div>
                      <FormField label="Quantité">
                        <Input
                          type="number"
                          value={crypto.amount}
                          onChange={(e) => updateAdditionalCrypto(index, 'amount', e.target.value)}
                          placeholder="0.00"
                          step="0.00000001"
                        />
                      </FormField>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Prix actuel (EUR)</label>
                        <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {crypto.currentPrice > 0 ? crypto.currentPrice.toLocaleString('fr-FR', {
                            style: 'currency',
                            currency: 'EUR'
                          }) : 'Chargement...'}
                        </div>
                      </div>
                      <div className="flex items-end">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => removeAdditionalCrypto(index)}
                          className="w-full"
                        >
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Ajouter une crypto manquante</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <div className="md:col-span-3">
                        <CryptoSelect
                          value={selectedCrypto}
                          onChange={setSelectedCrypto}
                          placeholder="Sélectionnez une crypto..."
                        />
                      </div>
                      <Button
                        onClick={handleAddCrypto}
                        disabled={!selectedCrypto}
                        className="w-full"
                      >
                        Ajouter
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="pt-4 border-t">
              <div className="text-lg font-semibold">
                Total portfolio: {totalPortfolioValue.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'EUR'
                })}
              </div>
            </div>
          </div>
        </Card>

        {/* Section Calculs */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Calculs Fiscaux</h2>

          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-blue-900">
                Valeur totale du portfolio
              </div>
              <div className="text-xl font-bold text-blue-600">
                {totalPortfolioValue.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'EUR'
                })}
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-purple-900">
                Coût d&apos;acquisition total
              </div>
              <div className="text-xl font-bold text-purple-600">
                {totalAcquisitionCost.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'EUR'
                })}
              </div>
              <div className="text-xs text-purple-700 mt-1">
                Payouts: valeur au payout • Achats: prix d&apos;achat • Ajouts manuels: 0€
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-yellow-900">Charges BNC déjà payées</div>
              <div className="text-xl font-bold text-yellow-600">
                {bncChargesPaid.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'EUR'
                })}
              </div>
              <div className="text-xs text-yellow-700 mt-1">
                34% des revenus BNC déclarés (payouts minés uniquement)
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-green-900">Plus-value latente</div>
              <div className="text-xl font-bold text-green-600">
                {Math.max(0, totalPortfolioValue - totalAcquisitionCost).toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'EUR'
                })}
              </div>
              <div className="text-xs text-green-700 mt-1">
                Valeur actuelle - Coût acquisition
              </div>
            </div>

            <div className="bg-indigo-50 p-4 rounded-lg border-2 border-indigo-200">
              <div className="text-sm font-medium text-indigo-900">💡 Simulation de vente</div>
              <div className="text-sm text-indigo-700 mt-2">
                Utilisez le simulateur de vente partielle ci-dessous pour calculer la flat tax sur vos ventes de cryptos
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Section Simulation de Vente */}
      <Card className="mt-8 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Simulateur de Vente Partielle
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Simulez la vente d&apos;une partie de vos tokens et calculez la flat tax correspondante
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="bg-indigo-50 p-4 rounded-lg">
              <h3 className="font-medium text-indigo-900 mb-3">Configuration de la vente</h3>

              <div className="space-y-4">
                <FormField label="Crypto à vendre">
                  <Select
                    value={selectedCryptoForSale}
                    onChange={(e) => {
                      setSelectedCryptoForSale(e.target.value);
                      setTokensToSell(0);
                    }}
                    options={[
                      { value: '', label: 'Sélectionnez une crypto...' },
                      ...availableCryptosForSale.map(crypto => ({
                        value: crypto.crypto.id,
                        label: `${crypto.crypto.name} (${crypto.crypto.symbol}) - ${crypto.totalTokens.toFixed(8)} disponibles`
                      }))
                    ]}
                  />
                </FormField>

                {selectedCryptoForSale && (() => {
                  const selected = availableCryptosForSale.find(c => c.crypto.id === selectedCryptoForSale);
                  return selected ? (
                    <>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Tokens disponibles:</span>
                          <div className="font-semibold">{selected.totalTokens.toFixed(8)}</div>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Prix actuel:</span>
                          <div className="font-semibold">
                            {selected.currentPrice.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                          </div>
                        </div>
                      </div>

                      <FormField label="Quantité à vendre">
                        <Input
                          type="number"
                          value={tokensToSell}
                          onChange={(e) => setTokensToSell(Number(e.target.value) || 0)}
                          placeholder="0.00000000"
                          step="0.00000001"
                          min="0"
                          max={selected.totalTokens}
                        />
                        <div className="mt-2 flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setTokensToSell(selected.totalTokens * 0.25)}
                          >
                            25%
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setTokensToSell(selected.totalTokens * 0.5)}
                          >
                            50%
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setTokensToSell(selected.totalTokens * 0.75)}
                          >
                            75%
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setTokensToSell(selected.totalTokens)}
                          >
                            100%
                          </Button>
                        </div>
                      </FormField>
                    </>
                  ) : null;
                })()}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Résultat de la vente</h3>

              {tokensToSell > 0 && selectedCryptoForSale ? (
                <div className="space-y-3">
                  <div className="bg-blue-50 p-3 rounded">
                    <div className="text-sm text-blue-900">Valeur de la vente</div>
                    <div className="text-xl font-bold text-blue-600">
                      {partialSaleCalculation.saleValue.toLocaleString('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      })}
                    </div>
                  </div>

                  <div className="bg-purple-50 p-3 rounded">
                    <div className="text-sm text-purple-900">Coût d&apos;acquisition</div>
                    <div className="text-xl font-bold text-purple-600">
                      {partialSaleCalculation.acquisitionCost.toLocaleString('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      })}
                    </div>
                  </div>

                  <div className="bg-green-50 p-3 rounded">
                    <div className="text-sm text-green-900">Plus-value</div>
                    <div className="text-xl font-bold text-green-600">
                      {partialSaleCalculation.plusValue.toLocaleString('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      })}
                    </div>
                  </div>

                  <div className="bg-red-50 p-3 rounded border-2 border-red-200">
                    <div className="text-sm text-red-900 font-medium">Flat Tax 31.4%</div>
                    <div className="text-2xl font-bold text-red-600">
                      {partialSaleCalculation.flatTax.toLocaleString('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      })}
                    </div>
                    <div className="text-xs text-red-700 mt-1">
                      À déclarer et payer
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Sélectionnez une crypto et saisissez une quantité pour voir les calculs
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
